import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { showUnexpectedErrorToast } from '@app/components/Toast';
// eslint-disable-next-line @lokalise/package-boundary -- should be refactored
import type { MigrateValues } from '@app/features/project/components/NewProject/components/MigrateForm';
import bugsnag from '@app/utils/bugsnag';

import fetchBaseQuery from '../fetchBaseQuery';

type CreateProject = {
    name: string;
    sourceLanguage: number;
    targetLanguages: number[];
    projectType: number;
    reviewing: boolean;
    isSegmentationEnabled: boolean;
    onboardingFlow: boolean;
    segmentationRulesType: string;
    segmentationRulesFile: File | undefined;
};

type CreateProjectResponse = {
    baseLanguage: number;
    projectId: string;
    projectName: string;
    projectRole: string;
    projectType: string;
    reviewing: boolean;
};

type MigrateProject = Omit<MigrateValues, 'service'> & {
    service: 'phraseapp' | 'transifex';
};

const validateStatus = (response: Response) => {
    if (response.status === 200) {
        return true;
    }

    if (response?.status === 400) {
        return false;
    }

    showUnexpectedErrorToast();
    bugsnag.log(`Create or Migrate Project error`);

    return false;
};

const buildFormDataFromCreateProjectPayload = (payload: CreateProject): FormData => {
    const formData = new FormData();

    formData.append('name', payload.name);
    formData.append('sourceLanguage', JSON.stringify(payload.sourceLanguage));

    payload.targetLanguages.forEach((targetLanguage) => {
        formData.append('targetLanguages[]', JSON.stringify(targetLanguage));
    });

    formData.append('projectType', JSON.stringify(payload.projectType));
    formData.append('reviewing', JSON.stringify(payload.reviewing));
    formData.append('onboardingFlow', JSON.stringify(payload.onboardingFlow));

    formData.append('isSegmentationEnabled', JSON.stringify(payload.isSegmentationEnabled));
    formData.append('segmentationRulesType', payload.segmentationRulesType);

    if (payload.segmentationRulesFile instanceof File) {
        formData.append('segmentationRulesFile', payload.segmentationRulesFile, payload.segmentationRulesFile.name);
    }

    return formData;
};

const buildFormDataFromMigrateProjectPayload = (payload: MigrateProject): FormData => {
    const formData = new FormData();

    formData.append('service', payload.service);

    formData.append('phraseappProjectId', payload.phraseappProjectId);
    formData.append('phraseappToken', payload.phraseappToken);
    formData.append('phraseappDropJobTags', payload.phraseappDropJobTags ? 'true' : 'false');
    formData.append('phraseappDropUploadTags', payload.phraseappDropUploadTags ? 'true' : 'false');

    formData.append('transifexProjectId', payload.transifexProjectId);
    formData.append('transifexToken', payload.transifexToken);

    formData.append('isSegmentationEnabled', JSON.stringify(payload.isSegmentationEnabled));
    formData.append('segmentationRulesType', payload.segmentationRulesType);

    if (payload.segmentationRulesFile instanceof File) {
        formData.append('segmentationRulesFile', payload.segmentationRulesFile, payload.segmentationRulesFile.name);
    }

    return formData;
};

const newProjectApi = createApi({
    reducerPath: 'projectsApi',
    baseQuery: fetchBaseQuery(),
    endpoints: (build) => ({
        createProject: build.mutation<CreateProjectResponse, CreateProject>({
            query: (payload) => ({
                headers: {
                    'x-auto-content-type': '1',
                },
                url: `/projects`,
                method: 'POST',
                body: buildFormDataFromCreateProjectPayload(payload),
                validateStatus,
            }),
        }),
        migrateProject: build.mutation<string, MigrateProject>({
            query: (payload) => ({
                headers: {
                    'x-auto-content-type': '1',
                },
                url: `/projects/migrate/${payload.service}`,
                method: 'POST',
                body: buildFormDataFromMigrateProjectPayload(payload),
                validateStatus,
            }),
            transformResponse: (projectId: string) => {
                // To give server some time to sync up
                setTimeout(() => {
                    window.location.assign(`/project/${projectId}/?view=multi`);
                }, 5000);

                return projectId;
            },
        }),
    }),
});

export const { useCreateProjectMutation, useMigrateProjectMutation } = newProjectApi;

export const newProjectMiddleware = newProjectApi.middleware;

export default newProjectApi;
